import { parseApiDate, toApiDate, type Dayjs } from "@marekl/adminer/datetime";
import type {
  Competition,
  CompetitionPayload,
  CompetitionType,
} from "../../lib/queries/competitions";
import type { ID } from "../../lib/id";

// Form state + converters for the shared competition form. Kept apart
// from the component file so fast refresh works there.

/** The changeover a competition gets when nobody says otherwise. Mirrors
 * `competition.DefaultStartGapSeconds`; the server is the authority. */
export const DEFAULT_START_GAP_SECONDS = 30;

export interface CompetitionFormState {
  name: string;
  type: CompetitionType;
  date: Dayjs | null;
  dateTo: Dayjs | null;
  venue: string;
  ruleSetId: string;
  seriesId: string; // "" = none, "new" = creating a new one
  newSeriesName: string;
  entryDeadline: Dayjs | null;
  /** The two uzávěrky that taper the club window. Null means „the rung
   * above" — category the entry deadline, roster the category — which
   * the server resolves, so an empty field is a real choice. */
  entryCategoryDeadline: Dayjs | null;
  entryRosterDeadline: Dayjs | null;
  /** The uzávěrka hudby. Null means "the same instant as the entry
   * deadline" — the server resolves the fallback, so an empty field here
   * is a real choice rather than a missing value. */
  musicDeadline: Dayjs | null;
  maxCapacity: string;
  /** Changeover between performances, in seconds, as typed. */
  startGapSeconds: string;
  organizerName: string;
  contactName: string;
  contactPhone: string;
  contactEmail: string;
}

export const emptyCompetitionForm: CompetitionFormState = {
  name: "",
  type: "mcr_qualification",
  date: null,
  dateTo: null,
  venue: "",
  ruleSetId: "",
  seriesId: "",
  newSeriesName: "",
  entryDeadline: null,
  entryCategoryDeadline: null,
  entryRosterDeadline: null,
  musicDeadline: null,
  maxCapacity: "",
  startGapSeconds: String(DEFAULT_START_GAP_SECONDS),
  organizerName: "",
  contactName: "",
  contactPhone: "",
  contactEmail: "",
};

export function competitionToForm(c: Competition): CompetitionFormState {
  return {
    name: c.name,
    type: c.type,
    date: parseApiDate(c.date),
    dateTo: c.date_to ? parseApiDate(c.date_to) : null,
    venue: c.venue,
    ruleSetId: String(c.rule_set_id),
    seriesId: c.series_id ? String(c.series_id) : "",
    newSeriesName: "",
    entryDeadline: c.entry_deadline ?? null,
    entryCategoryDeadline: c.entry_category_deadline ?? null,
    entryRosterDeadline: c.entry_roster_deadline ?? null,
    musicDeadline: c.music_deadline ?? null,
    maxCapacity: c.max_capacity ? String(c.max_capacity) : "",
    startGapSeconds: String(c.start_gap_seconds || DEFAULT_START_GAP_SECONDS),
    organizerName: c.organizer_name,
    contactName: c.contact_name,
    contactPhone: c.contact_phone,
    contactEmail: c.contact_email,
  };
}

// competitionFormPayload builds the wire payload. seriesId is passed
// separately because a "new" series is created by the caller first.
export function competitionFormPayload(
  form: CompetitionFormState,
  seasonId: ID,
  seriesId: ID | null,
): CompetitionPayload {
  return {
    season_id: seasonId,
    rule_set_id: form.ruleSetId,
    series_id: seriesId,
    type: form.type,
    name: form.name.trim(),
    date: toApiDate(form.date) ?? "",
    date_to: toApiDate(form.dateTo) ?? "",
    venue: form.venue.trim(),
    organizer_name: form.organizerName.trim(),
    contact_name: form.contactName.trim(),
    contact_phone: form.contactPhone.trim(),
    contact_email: form.contactEmail.trim(),
    entry_deadline: form.entryDeadline ? form.entryDeadline.toISOString() : null,
    entry_category_deadline: form.entryCategoryDeadline
      ? form.entryCategoryDeadline.toISOString()
      : null,
    entry_roster_deadline: form.entryRosterDeadline
      ? form.entryRosterDeadline.toISOString()
      : null,
    music_deadline: form.musicDeadline ? form.musicDeadline.toISOString() : null,
    max_capacity: form.maxCapacity ? Number(form.maxCapacity) : null,
    // Zero means "not set" on the wire, and the server substitutes the
    // default — a day scheduled with no changeover at all is not a
    // timetable anyone could run.
    start_gap_seconds: Number(form.startGapSeconds) || 0,
  };
}

export function canSubmitCompetitionForm(form: CompetitionFormState): boolean {
  return (
    !!form.name.trim() &&
    !!form.date &&
    !!form.ruleSetId &&
    (form.seriesId !== "new" || !!form.newSeriesName.trim())
  );
}
